import * as React from 'react'
import * as ReactTable from 'react-table-6'
import 'react-table-6/react-table.css'
import cx from 'classnames'
import styles from './data.grid.styl'
import FilteringOptions, {
  FilteringOptionsProps,
  SelectFilterOption,
  FilterStates
} from '../filtering.options'
import checkboxHOC, { RTSelectTableProps } from './hocs/selectTable'
import searchingDataView from '@src/utils/filtering.data'
import { separateDataBySeverity, columnMapper, isDisabled } from './helpers'
import { CSSSelector } from '@src/utils/css.selector.helper'

import ImprovedPagination from './components/pagination'

import type {
  DataGridProps,
  FilterOptions,
  ExtColumn,
  GridAction,
  ColumnProps,
  ExtRowInfo,
  PluginProps,
  State
} from './models'

import {
  ColumnExternalType,
  ColumnExtraType,
  ColumnInternalType
} from './models'

import { StyledTableWrapper, HeaderProps } from './components'

import {
  uniq,
  map,
  filter,
  isEmpty,
  isEqual,
  some,
  find,
  includes,
  compact
} from 'lodash'
import type { ValueType } from '../common/select'
import type { ResourceTypeDict } from '@src/redux/reducers/shell'
import { SortingRule } from 'react-table-6'
import { DownloadButton } from '../download'
import uuid from 'uuid'
import { useEffect } from 'react'
import ActionsPlugin from './components/actions.plugin'
import Header from './components/header.grid'
import { useEffectUpdate, useStateCallback } from '@src/hooks'
export { Header }
export { ColumnProps, GridAction, DataGridProps }

const Table = checkboxHOC(ReactTable)

const defaultSelectedOption: ValueType<SelectFilterOption> = {
  value: 'ALL',
  label: 'ALL'
}

const DataGridCSSSelectors = {
  gridHeader: new CSSSelector('dataGridHeader'),
  expandedContent: new CSSSelector('expandedContent')
}

function DataGrid<T, PluginOptions>(props: DataGridProps<T, PluginOptions>) {
  const {
    onCheckChange,
    data,
    keyField,
    serverPaging,
    resourceTypes,
    onClickRow,
    isSelectedRow,
    totalCount,
    onSortedChange,
    onPrevPage,
    loading,
    gridId,
    filterOptions,
    title,
    showCount,
    efficientPaging,
    defaultSorted,
    pages,
    canNext,
    getTrProps,
    canPrev,
    pluginOptions,
    useAdvancedPagination,
    noSelectAll,
    downloadOptions,
    onNextPage,
    plugin: Plugin,
    height: heightReactTable,
    columns = [],
    actions = [],
    showPagination = true,
    showPageSizeOptions = true,
    hideEmpty = false,
    hideEmptyColumns = false,
    sortable = true,
    rowsPerPage = 20,
    delayedSearch = false,
    delayForSearch = 300
  } = props

  const headerUpdate = React.useRef(null)
  const checkboxTable = React.useRef(null)

  const [state, setState] = React.useState<State<T>>({
    changeUID: uuid(),
    expandedColumnField: null,
    expanded: {},
    page: props.page,
    pageSize: rowsPerPage,
    data: props.data,
    filter: FilterStates.all,
    selection: props.selection || [],
    selectAll: isAllSelected(
      props.keyField,
      props.data || [],
      props.selection || []
    ),
    selectedFilter: {
      selectedOption: defaultSelectedOption,
      column: null
    }
  })
  const [filteredState, setFilteredState] = React.useState({
    filtered: [] as ReactTable.Filter[]
  })
  const [search, setSearch] = useStateCallback('')

  useEffect(() => {
    onUpdate(props, state)
  }, [props])

  function onExpandRow(viewIndex: number, field: keyof T) {
    setState(prev => {
      const { expanded, page = props.page || 0, pageSize } = prev
      const rowIndex = pageSize * page + viewIndex
      return {
        ...prev,
        expandedColumnField: field,
        expanded: {
          ...expanded,
          [rowIndex]: !expanded[rowIndex]
        }
      }
    })
  }

  function toggleSelection(key: T[keyof T], shift: boolean, row: T) {
    const { data, selection: stateSelection = [] } = state
    let selection = [...stateSelection]
    const keyIndex = selection.indexOf(key)
    if (keyIndex >= 0) {
      selection = [
        ...selection.slice(0, keyIndex),
        ...selection.slice(keyIndex + 1)
      ]
    } else {
      selection.push(key)
    }
    setState(prev => ({
      ...prev,
      selection,
      selectAll: isAllSelected(keyField, data || [], selection || [])
    }))
    if (onCheckChange) {
      onCheckChange(selection)
    }
  }

  function onUpdate(props, state) {
    const changedData = !isEqual(state?.data, props?.data)
    const changedPageSize = !isEqual(state.pageSize, rowsPerPage)
    const changedPage =
      props.page !== void 0 && !isEqual(state.page, props.page)
    const changedSelection =
      props.selection && !isEqual(state.selection, props.selection)
    if (changedData) {
      setState(prev => ({
        ...prev,
        expandedColumnField: changedPage ? state.expandedColumnField : null,
        expanded: changedPage ? state.expanded : {},
        page: props.page || 0,
        pageSize: changedPageSize ? rowsPerPage : state?.pageSize,
        data: props.data,
        filter: changedData ? FilterStates.all : state.filter,
        changeUID: uuid(),
        selectedFilter: {
          selectedOption: defaultSelectedOption,
          column: null,
          data: null
        },
        filtered: []
      }))
      setSearch('')
    }
    if (changedSelection) {
      const nextSelection = props.selection || []
      setState(prev => ({
        ...prev,
        selection: nextSelection,
        selectAll: isAllSelected(
          props.keyField as never,
          props.data,
          nextSelection
        )
      }))
    }
  }

  function selectFilter<T>(
    items: T[],
    column: keyof T,
    searchValue: T[keyof T] | React.ReactText
  ) {
    return filter(items, item => item[column] === searchValue)
  }

  function onSearchChange({ searchValue }: { searchValue: string }) {
    setState(state => ({
      ...state,
      expanded: {}
    }))
    setSearch(searchValue, () => {
      const wrappedInstance = checkboxTable?.current?.getWrappedInstance()
      const allVisibleColumns =
        wrappedInstance.getResolvedState().allVisibleColumns

      const { 0: first } = allVisibleColumns?.filter(f => f.filterable)
      wrappedInstance.filterColumn(first, searchValue)
    })
  }

  // function getResolvedData() {
  //   if (!checkboxTable?.current) return []
  //   const wrappedInstance = checkboxTable?.current?.getWrappedInstance()
  //   return wrappedInstance.getResolvedState().resolvedData
  // }

  function getResolvedState() {
    if (!checkboxTable?.current) return {}
    const wrappedInstance = checkboxTable?.current?.getWrappedInstance()
    return wrappedInstance.getResolvedState()
  }

  function onTypeChange(type: ValueType<SelectFilterOption>) {
    const { columns = [] } = getResolvedState()
    const filterColumn = getSelectFilterColumn(columns)
    if (filterColumn && (type as SelectFilterOption)?.value !== 'ALL') {
      const col: keyof T = (String(filterColumn.accessor) ||
        filterColumn.id) as keyof T
      setState(prev => ({
        ...prev,
        selectedFilter: {
          data: selectFilter(data, col, (type as SelectFilterOption)?.value),
          column: col,
          selectedOption: type
        },
        page: 0,
        expanded: {}
      }))
    } else {
      setState(prev => ({
        ...prev,
        selectedFilter: {
          data: null,
          selectedOption: defaultSelectedOption
        },
        page: 0,
        expanded: {}
      }))
    }
  }

  function toggleAll() {
    const selectAll = !state.selectAll
    const selection = []
    if (selectAll) {
      const wrappedInstance = checkboxTable?.current?.getWrappedInstance()
      const currentRecords = wrappedInstance.getResolvedState().sortedData
      const selectedColumns = getSelectColumn(columns)
      const filtered = filter(
        currentRecords || [],
        x => !isDisabled(selectedColumns, disable => disable(x))
      )
      filtered.forEach(item => {
        selection.push(item._original[keyField])
      })
    }
    setState(prev => ({ ...prev, selectAll, selection }))
    if (onCheckChange) {
      onCheckChange(selection)
    }
  }

  function isSelected(key) {
    return state.selection?.includes(key)
  }

  function isAllSelected<T>(
    keyField: keyof T,
    data: T[] = [],
    selection: any[] = []
  ) {
    return (
      !isEmpty(data) &&
      !isEmpty(selection) &&
      !some(data, item => !includes(selection, item[keyField]))
    )
  }

  function showAll() {
    setState(prev => ({ ...prev, filter: FilterStates.all, expanded: {} }))
  }

  function showRed() {
    setState(prev => ({
      ...prev,
      filter: FilterStates.redItems,
      expanded: {}
    }))
  }

  function showOrange() {
    setState(prev => ({
      ...prev,
      filter: FilterStates.orangeItems,
      expanded: {}
    }))
  }

  function showYellow() {
    setState(prev => ({
      ...prev,
      filter: FilterStates.yellowItems,
      expanded: {}
    }))
  }

  useEffectUpdate(() => {
    updateHeader()
  })

  function getExpanderColumn<T>(columns: ColumnProps<T>[], field: keyof T) {
    return find(columns, c => c.field === field)
  }

  function getSelectColumn<T>(columns: ColumnProps<T>[]) {
    return find(columns, c => c.type === ColumnInternalType.selector)
  }

  function getSelectFilterColumn<T>(columns: ExtColumn<T>[]) {
    const { 0: filterColumn } = columns.filter(
      f => f.columnType === 'selectFilter'
    )
    return filterColumn
  }

  function defaultFilterMethod(
    filter: ReactTable.Filter,
    rows: T[],
    column?: ExtColumn<T>[]
  ) {
    if (serverPaging) {
      return rows
    }
    const result = searchingDataView(
      rows,
      (columns || []).filter(x => !x.hide),
      filter.value
    )
    return result
  }

  function updateHeader() {
    headerUpdate.current = Object.create({})
  }

  function getDropdownOptions(
    data: T[],
    mappedColumns: ExtColumn<T>[],
    filterOptions: FilterOptions
  ): SelectFilterOption[] {
    const {
      useGridData = true,
      hasObjectTypeFilter,
      hasIdentityTypeFilter
    } = filterOptions

    if (useGridData) {
      const filterColumn = getSelectFilterColumn(mappedColumns)
      return !isEmpty(data) &&
        filterColumn &&
        filterColumn.accessor &&
        !isEmpty(resourceTypes)
        ? uniq(
            compact(map(data, item => item[String(filterColumn.accessor)]))
          ).map(type => {
            const rtype = resourceTypes[type] || ({} as ResourceTypeDict)
            const { display_name } = rtype
            const label = display_name || type
            return {
              value: type as string,
              label: label.toUpperCase()
            }
          })
        : []
    }
    if (hasObjectTypeFilter) {
      return (
        filter(resourceTypes, t => t.object_type).map(x => ({
          value: x.resource_name,
          label: x.display_name
        })) || []
      )
    }
    if (hasIdentityTypeFilter) {
      return (
        filter(resourceTypes, t => t.identity_type).map(x => ({
          value: x.resource_name,
          label: x.display_name
        })) || []
      )
    }
    return []
  }

  function defaultGetTrGroupProps(
    state: ReactTable.FinalState<T>,
    rowInfo: ExtRowInfo<T>,
    column: ExtColumn<T>
  ) {
    return getClickableTrProps(state, rowInfo, column)
  }

  function getClickableTrProps(
    finalState: ReactTable.FinalState<T>,
    rowInfo?: ExtRowInfo<T>,
    column?: ExtColumn<T>
  ) {
    const hasSelectedRowFn =
      isSelectedRow && typeof isSelectedRow === 'function'
    const selected =
      rowInfo && hasSelectedRowFn ? isSelectedRow(rowInfo) : false
    const hasOnClickRow = onClickRow && typeof onClickRow === 'function'
    if (rowInfo && hasOnClickRow) {
      const { styles } = props
      return {
        onClick: (e, handleOriginal) => {
          onClickRow(rowInfo, finalState, column, e, handleOriginal)
        },
        style: {
          cursor: 'pointer',
          backgroundColor: selected ? 'rgba(212, 237, 245, 0.5)' : null,
          ...(selected && styles && styles.selectedRow)
        }
      }
    }
    return {}
  }

  function defaultGetTrProps(
    finalState: ReactTable.FinalState<T>,
    rowInfo?: ExtRowInfo<T>,
    column?: ExtColumn<T>
  ) {
    const selected = rowInfo ? isSelected(rowInfo.original[keyField]) : false
    return {
      style: {
        backgroundColor: selected ? 'rgba(212, 237, 245, 0.5)' : null
      }
    }
  }

  function headerCount(): number {
    if (serverPaging && !!totalCount) return totalCount
    if (!checkboxTable?.current) return 0
    const wrappedInstance = checkboxTable?.current?.getWrappedInstance()
    const sortedData = wrappedInstance.getResolvedState().sortedData
    return selectedFilter?.data?.length || sortedData.length || 0
  }

  function onFetchData(
    gridState: ReactTable.FinalState<T>,
    instance: ReactTable.Instance<T>
  ) {
    if (typeof props.onFetchData === 'function') {
      props.onFetchData(gridState, instance)
    }
    updateHeader()
  }

  function onPageChange(page?: number) {
    setState(prev => ({ ...prev, page }))
  }

  function onPageSizeChange(pageSize: number, page: number) {
    setState(state => ({ ...state, page, pageSize }))
  }

  function onSortChange(
    newSorted?: SortingRule[],
    column?: any,
    additive?: boolean
  ) {
    if (typeof onSortedChange === 'function') {
      onSortedChange(newSorted, column, additive)
    }

    setState(state => ({
      ...state,
      expanded: {},
      expandedColumnField: null
    }))
  }

  function getExpandedRows() {
    const { expanded = {}, pageSize, page = props.page || 0 } = state
    const expandedRows = {}
    Object.keys(expanded).forEach(indexRow => {
      expandedRows[Number(indexRow) - pageSize * page] = expanded[indexRow]
    })
    return expandedRows
  }

  function handlePrev(event: React.MouseEvent<HTMLButtonElement, MouseEvent>) {
    const wrappedInstance = checkboxTable?.current?.getWrappedInstance()
    const resolvedState = wrappedInstance.getResolvedState()
    if (typeof onPrevPage === 'function' && !loading) {
      onPrevPage(resolvedState, wrappedInstance, event)
    }
  }

  function handleNext(event: React.MouseEvent<HTMLButtonElement, MouseEvent>) {
    const wrappedInstance = checkboxTable?.current?.getWrappedInstance()
    const resolvedState = wrappedInstance.getResolvedState()
    if (typeof onNextPage === 'function' && !loading) {
      onNextPage(resolvedState, wrappedInstance, event)
    }
  }

  const { selectAll, selectedFilter, expandedColumnField, selection } = state

  const emptyData = data && !(data.length > 0)

  // don't display grid if its empty
  const needHideGrid = emptyData && hideEmpty

  // don't render grid if its empty and we don't have 'onFetchData'
  if (needHideGrid && typeof props.onFetchData !== 'function') return null

  const mappedColumns = columnMapper({
    columns,
    resolvedState: getResolvedState,
    resourceTypes,
    hideEmptyColumns,
    data,
    keyField,
    onExpandRow,
    actions
  })

  const { redList, orangeList, yellowList } = separateDataBySeverity(data)
  let currentList: T[] = data
  const redCount = redList.length
  const orangeCount = orangeList.length
  const yellowCount = yellowList.length

  if (state.filter === FilterStates.redItems) {
    currentList = redList
  } else if (state.filter === FilterStates.orangeItems) {
    currentList = orangeList
  } else if (state.filter === FilterStates.yellowItems) {
    currentList = yellowList
  }

  if (selectedFilter?.data) {
    currentList = selectedFilter.data
  }

  if (useAdvancedPagination) {
    Object.assign(ReactTable.ReactTableDefaults, {
      PaginationComponent: ImprovedPagination
    })
  }

  // if server paging enabled then we should handle severity filter out of grid
  let filterOptionsProps: FilteringOptionsProps = filterOptions
    ? {
        ...filterOptions,
        severityFilter:
          filterOptions.severityFilter ??
          !!find(columns || [], c => c.type === ColumnInternalType.severity),
        selectedIndicator: state.filter,
        selectedOption: selectedFilter?.selectedOption,
        handleChange: onSearchChange,
        handleTypeChange: onTypeChange,
        options: getDropdownOptions(data, mappedColumns, filterOptions)
      }
    : {}

  const checkboxProps: RTSelectTableProps<T> = {
    ...ReactTable.ReactTableDefaults,
    className: '-striped -highlight',
    selectAll,
    isSelected,
    toggleSelection,
    toggleAll,
    getTrProps: (
      state: ReactTable.FinalState<T>,
      rowInfo: ExtRowInfo<T>,
      column: ExtColumn<T>,
      instance: ReactTable.Instance<T>
    ) =>
      typeof getTrProps === 'function'
        ? getTrProps(state, rowInfo, column, instance, defaultGetTrProps)
        : defaultGetTrProps(state, rowInfo, column),
    getTrGroupProps: defaultGetTrGroupProps
  }
  //check if we have column with expander
  const expanderColumn = getExpanderColumn(columns, expandedColumnField)

  const extraTableProps: Partial<RTSelectTableProps<T>> = {
    expanded: getExpandedRows(),
    ///By adding a SubComponent props, you can easily add an expansion level to all root-level rows:
    // It is
    SubComponent:
      //We check whether we have a column expander with options
      expanderColumn &&
      expanderColumn.expanderOptions &&
      expanderColumn.expanderOptions.contentTemplate
        ? //if we have expander and options from this column`s props, use contentTemplate
          //contentTemplate it is function, that accepts a row and a column
          (row: ExtRowInfo<T>) => (
            <div
              data-column-field={expanderColumn.field}
              data-row-view-index={row.viewIndex}
              role={DataGridCSSSelectors.expandedContent.value}
            >
              {expanderColumn.expanderOptions.contentTemplate(
                row,
                expanderColumn
              )}
            </div>
          )
        : // not use SubComponent, if don't have template with content, expander options and expander column
          null
  }

  let extraHeaderProps: Partial<HeaderProps<T>> = {}

  if (!serverPaging) {
    extraHeaderProps = {
      ...extraHeaderProps,
      searchValue: search
    }

    filterOptionsProps = {
      ...filterOptionsProps,
      changeUID: state.changeUID,
      searchValue: search,
      redItemsCount: redCount,
      orangeItemsCount: orangeCount,
      yellowItemsCount: yellowCount,
      allOptionCallback: showAll,
      redItemsCallback: showRed,
      orangeItemsCallback: showOrange,
      yellowItemsCallback: showYellow,
      delayedSearch,
      delayForSearch
    }

    extraTableProps.page = state.page
    extraTableProps.onPageChange = onPageChange
    extraTableProps.onPageSizeChange = onPageSizeChange
  } else {
    extraTableProps.page = props.page
    extraTableProps.manual = true
  }

  // if we have default searchValue
  const { searchValue } = filterOptions || ({} as FilterOptions)
  if (!isEmpty(searchValue)) {
    const { 0: firstVisibleColumn } = mappedColumns.filter(
      f => f.filterable && f.show && f.id
    )
    const { id } = firstVisibleColumn || ({} as ExtColumn<T>)
    extraTableProps.defaultFiltered = [{ id, value: searchValue }]
  }

  if (efficientPaging) {
    const {
      pageText = '',
      ofText = '',
      currentPageComponent = page => undefined,
      totalPagesCountComponent = page => undefined
    } = props
    extraTableProps.manual = true

    extraTableProps.renderCurrentPage = currentPageComponent
    extraTableProps.pageText = pageText
    extraTableProps.ofText = ofText
    extraTableProps.renderTotalPagesCount = totalPagesCountComponent

    extraTableProps.NextComponent = nextProps => (
      <button
        role={'grid_next_page'}
        className="-btn"
        disabled={!canNext}
        onClick={handleNext}
      >
        {nextProps?.children}
      </button>
    )
    extraTableProps.PreviousComponent = previousProps => (
      <button
        role={'grid_prev_page'}
        className="-btn"
        disabled={!canPrev}
        onClick={handlePrev}
      >
        {previousProps.children}
      </button>
    )
  }

  const pluginProps: PluginProps<T, PluginOptions> = {
    resolvedState: getResolvedState,
    columns,
    loading,
    keyField,
    selectedItems: selection,
    items: currentList,
    options: pluginOptions,
    totalCount
  }

  return (
    <div
      data-role={'data-grid'}
      id={gridId}
      className={cx(styles.gridWraper, {
        [styles.hidden]: needHideGrid,
        pending: loading
      })}
    >
      <Header
        {...extraHeaderProps}
        title={title}
        update={headerUpdate}
        state={state}
        showCount={showCount}
        className={cx(DataGridCSSSelectors.gridHeader.value, styles.title)}
        count={headerCount}
      />
      <div className={styles.optionsWrapper}>
        {filterOptions ? (
          <FilteringOptions
            role={`data-grid-filtering`}
            {...filterOptionsProps}
          />
        ) : null}
        {Plugin && <Plugin {...pluginProps} />}
        <ActionsPlugin {...pluginProps} title="Action" actions={actions} />
        {downloadOptions && (
          <DownloadButton
            getResolvedState={getResolvedState}
            downloadOptions={downloadOptions}
            columns={mappedColumns}
            filterOptions={filterOptions}
            filterOptionsProps={filterOptionsProps}
            searchValue={extraHeaderProps?.searchValue}
          />
        )}
      </div>
      <StyledTableWrapper role={'grid_wrapper'}>
        <Table
          role={'grid'}
          keyField={keyField}
          ref={checkboxTable}
          {...checkboxProps}
          data={currentList}
          showFilters={false}
          columns={mappedColumns}
          pageSize={state?.pageSize}
          defaultPageSize={rowsPerPage}
          style={{
            height: heightReactTable
          }}
          showPageJump={false}
          showPagination={showPagination}
          showPageSizeOptions={showPageSizeOptions}
          defaultFilterMethod={defaultFilterMethod as any}
          defaultSorted={defaultSorted}
          filterAll={false}
          filterable={false}
          onFetchData={onFetchData}
          pages={pages}
          sortable={sortable}
          loading={loading}
          noSelectAll={noSelectAll}
          onSortedChange={onSortChange}
          onFilteredChange={filtered => {
            if (!serverPaging) {
              setState(prev => ({ ...prev, page: 0 }))
            }
            setFilteredState(prev => ({ ...prev, filtered }))
            updateHeader()
          }}
          filtered={filteredState.filtered}
          {...extraTableProps}
        />
      </StyledTableWrapper>
    </div>
  )
}

export const ColumnType = {
  ...ColumnExternalType,
  ...ColumnExtraType,
  ...ColumnInternalType
}

export default React.memo(DataGrid)
